<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="bg-white shadow-lg rounded-lg">
        <!-- Title and Actions -->
        <div class="p-6">
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
      <h2 class="text-2xl font-bold text-[#00225E] mb-4 sm:mb-0">
        Invoice Management
      </h2>
      <div class="flex space-x-3">
        <button
          @click="viewSubmitInvoice"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#00225E] hover:bg-[#00225E]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00225E] transition-colors duration-200"
        >
          Submit Invoice
        </button>
        
        <button
          v-if="isAdmin"
          @click="viewUserManagement"
          class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00225E] transition-colors duration-200"
        >
          User Management
        </button>
      </div>
  </div>

    <!-- Company filter (owners / superadmins only) -->
    <div v-if="canSeeAllData" class="flex items-center gap-3 mb-6">
      <label for="company" class="text-sm font-medium text-gray-700">Company</label>
      <select
        id="company"
        v-model="selectedCompany"
        @change="loadData"
        class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00225E] min-w-[180px]"
      >
        <option value="ALL">All companies</option>
        <option v-for="c in companies" :key="c" :value="c">{{ c }}</option>
      </select>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200">
      <nav class="-mb-px flex space-x-8">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            activeTab === tab.id
              ? 'border-[#00225E] text-[#00225E]'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
          ]"
        >
          {{ tab.name }}
        </button>
      </nav>
    </div>

    <!-- Metrics -->
    <InvoiceMetrics
      :approvedWOData="approvedWOData"
      :submittedInvoicesData="submittedInvoicesData"
      :paidInvoicesData="paidInvoicesData"
      :returnInvoicesData="returnInvoicesData"
      :userRole="userRole"
    />

        <!-- Loading Indicator -->
        <div v-if="loading" class="mt-6 flex justify-center items-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00225E]"></div>
          <span class="ml-2 text-gray-600">Loading data...</span>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-blue-800">Error loading data</h3>
              <div class="mt-2 text-sm text-blue-700">
                <p>{{ error }}</p>
              </div>
              <div class="mt-4">
                <button @click="loadData" class="bg-blue-100 px-3 py-2 rounded-md text-sm font-medium text-blue-800 hover:bg-blue-200">
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab Content -->
        <div v-if="!loading && !error" class="mt-6">
          <!-- Approved WO Tab -->
          <div v-if="activeTab === 'approved-wo'" class="space-y-4">
            <ApprovedWOTable :data="approvedWOData" @refresh="refreshApprovedWO" />
          </div>

          <!-- Invoices Submitted Tab -->
          <div v-if="activeTab === 'invoices-submitted'" class="space-y-4">
            <InvoicesSubmittedTable :data="submittedInvoicesData" @refresh="refreshSubmittedInvoices" />
          </div>

          <!-- Invoices Paid Tab -->
          <div v-if="activeTab === 'invoices-paid'" class="space-y-4">
            <InvoicesPaidTable :data="paidInvoicesData" @refresh="refreshPaidInvoices" />
          </div>

          <!-- Reject/Dispute Tab -->
          <div v-if="activeTab === 'invoices-declined'" class="space-y-4">
            <InvoicesDeclinedTable :data="declinedInvoicesData" @refresh="refreshDeclinedInvoices" />
          </div>

          <!-- Invoices Return Tab -->
          <div v-if="activeTab === 'invoices-return'" class="space-y-4">
            <InvoicesReturnTable :data="returnInvoicesData" @refresh="refreshReturnInvoices" />
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import ApprovedWOTable from "../components/ApprovedWOTable.vue";
import InvoicesSubmittedTable from "../components/InvoicesSubmittedTable.vue";
import InvoicesPaidTable from "../components/InvoicesPaidTable.vue";
import InvoicesDeclinedTable from "../components/InvoicesDeclinedTable.vue";
import InvoicesReturnTable from "../components/InvoicesReturnTable.vue";
import InvoiceMetrics from "../components/InvoiceMetrics.vue";

export default {
  components: {
    ApprovedWOTable,
    InvoicesSubmittedTable,
    InvoicesPaidTable,
    InvoicesDeclinedTable,
    InvoicesReturnTable,
    InvoiceMetrics
  },
  setup() {
    const store = useStore();
    const activeTab = ref("approved-wo");
    
    // Get user role and permissions
    const userRole = computed(() => store.getters.userRole);
    const isAdmin = computed(() => store.getters.isAdmin);
    const canSeeAllData = computed(() => store.getters.canSeeAllData);
    
    // Define tabs based on user role
    const allTabs = [
      { id: "approved-wo", name: "Approved WO", roles: ['user', 'admin', 'owner', 'superadmin'] },
      { id: "invoices-submitted", name: "Invoices Submitted", roles: ['user', 'admin', 'owner', 'superadmin'] },
      { id: "invoices-paid", name: "Invoices Paid", roles: ['admin', 'owner', 'superadmin'] },
      { id: "invoices-declined", name: "Reject/Dispute", roles: ['user', 'admin', 'owner', 'superadmin'] },
      { id: "invoices-return", name: "Invoices Return", roles: ['user', 'admin', 'owner', 'superadmin'] }
    ];
    
    // Filter tabs based on user role
    const tabs = computed(() => {
      return allTabs.filter(tab => tab.roles.includes(userRole.value));
    });

    // Data from API
    const approvedWOData = ref([]);
    const submittedInvoicesData = ref([]);
    const paidInvoicesData = ref([]);
    const declinedInvoicesData = ref([]);
    const returnInvoicesData = ref([]);
    const loading = ref(false);
    const error = ref(null);

    // Company filter (owners / superadmins only)
    const companies = ref([]);
    const selectedCompany = ref("ALL");

    // Build the query filters shared by every invoice request
    const buildFilters = () => {
      if (canSeeAllData.value && selectedCompany.value !== "ALL") {
        return { company: selectedCompany.value };
      }
      return {};
    };

    // Load the list of companies for the selector
    const loadCompanies = async () => {
      if (!canSeeAllData.value) return;
      try {
        const response = await store.dispatch("getCompanies");
        if (response.ok) {
          companies.value = response.data || [];
        }
      } catch (err) {
        console.error("Error loading companies:", err);
      }
    };

    // Load data from API
    const loadData = async () => {
      loading.value = true;
      error.value = null;

      try {
        const filters = buildFilters();
        const [approvedWOResponse, submittedResponse, paidResponse, declinedResponse, returnResponse] = await Promise.all([
          store.dispatch("getApprovedWorkOrders", filters),
          store.dispatch("getSubmittedInvoices", filters),
          store.dispatch("getPaidInvoices", filters),
          store.dispatch("getDeclinedInvoices", filters),
          store.dispatch("getReturnInvoices", filters)
        ]);

        if (approvedWOResponse.ok) {
          approvedWOData.value = approvedWOResponse.data;
        } else {
          console.error("Error loading approved work orders:", approvedWOResponse.error);
        }

        if (submittedResponse.ok) {
          submittedInvoicesData.value = submittedResponse.data;
        } else {
          console.error("Error loading submitted invoices:", submittedResponse.error);
        }

        if (paidResponse.ok) {
          paidInvoicesData.value = paidResponse.data;
        } else {
          console.error("Error loading paid invoices:", paidResponse.error);
        }

        if (declinedResponse.ok) {
          declinedInvoicesData.value = declinedResponse.data;
        } else {
          console.error("Error loading declined invoices:", declinedResponse.error);
        }

        if (returnResponse.ok) {
          returnInvoicesData.value = returnResponse.data;
        } else {
          console.error("Error loading return invoices:", returnResponse.error);
        }
      } catch (err) {
        error.value = "Failed to load data from server";
        console.error("Error loading data:", err);
      } finally {
        loading.value = false;
      }
    };

    const viewDashboard = () => {
      
    };

    const viewSubmitInvoice = () => {
      window.open("https://mic-billing-ap.smartsheetexperts.com/", "_blank");
    };

    const viewUserManagement = () => {
      // Navigate to user management page
      window.location.href = '/admin/users';
    };

    // Individual refresh functions for each table
    const refreshApprovedWO = async () => {
      console.log('Refreshing Approved Work Orders...');
      try {
        const response = await store.dispatch("getApprovedWorkOrders", buildFilters());
        if (response.ok) {
          approvedWOData.value = response.data;
          console.log('Approved WO data refreshed successfully');
        }
      } catch (error) {
        console.error('Error refreshing approved WO:', error);
      }
    };

    const refreshSubmittedInvoices = async () => {
      console.log('Refreshing Submitted Invoices...');
      try {
        const response = await store.dispatch("getSubmittedInvoices", buildFilters());
        if (response.ok) {
          submittedInvoicesData.value = response.data;
          console.log('Submitted invoices refreshed successfully');
        }
      } catch (error) {
        console.error('Error refreshing submitted invoices:', error);
      }
    };

    const refreshPaidInvoices = async () => {
      console.log('Refreshing Paid Invoices...');
      try {
        const response = await store.dispatch("getPaidInvoices", buildFilters());
        if (response.ok) {
          paidInvoicesData.value = response.data;
          console.log('Paid invoices refreshed successfully');
        }
      } catch (error) {
        console.error('Error refreshing paid invoices:', error);
      }
    };

    const refreshDeclinedInvoices = async () => {
      console.log('Refreshing Declined Invoices...');
      try {
        const response = await store.dispatch("getDeclinedInvoices", buildFilters());
        if (response.ok) {
          declinedInvoicesData.value = response.data;
          console.log('Declined invoices refreshed successfully');
        }
      } catch (error) {
        console.error('Error refreshing declined invoices:', error);
      }
    };

    const refreshReturnInvoices = async () => {
      console.log('Refreshing Return Invoices...');
      try {
        const response = await store.dispatch("getReturnInvoices", buildFilters());
        if (response.ok) {
          returnInvoicesData.value = response.data;
          console.log('Return invoices refreshed successfully');
        }
      } catch (error) {
        console.error('Error refreshing return invoices:', error);
      }
    };

    onMounted(() => {
      loadCompanies();
      loadData();
    });

    return {
      activeTab,
      tabs,
      userRole,
      isAdmin,
      canSeeAllData,
      companies,
      selectedCompany,
      approvedWOData,
      submittedInvoicesData,
      paidInvoicesData,
      declinedInvoicesData,
      returnInvoicesData,
      loading,
      error,
      viewDashboard,
      viewSubmitInvoice,
      viewUserManagement,
      loadData,
      refreshApprovedWO,
      refreshSubmittedInvoices,
      refreshPaidInvoices,
      refreshDeclinedInvoices,
      refreshReturnInvoices
    };
  }
};
</script>
